import { memo } from 'react'

import { AvcOutputConfig } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FormStructure } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Task/Config'
import { avcInstanceQueryKeys } from 'services/queryKeys'

type SecondaryInformationProps = {
  output: AvcOutputConfig
}

const SecondaryInformationRaw = ({ output }: SecondaryInformationProps) => {
  const { t } = useTranslation(['domain'])

  const { watch: watchAvcTask } = useFormContext<FormStructure>()

  const url = output.hls?.url ?? output.rtmp?.url ?? output.rtsp?.url ?? output.srt?.url ?? output.udp?.url
  const sdi = output.sdi
  const ndi = output.ndi
  const physicalOutput = sdi ?? ndi

  const watchedInstanceIds = watchAvcTask('config.executor.instanceIds')

  const avcInstancesBatchQuery = useQuery({
    ...avcInstanceQueryKeys.batchGet({ avcInstanceBatchGet: { ids: watchedInstanceIds! } }),
    enabled: !!watchedInstanceIds?.length && !!physicalOutput,
  })

  if (url) {
    if (!URL.canParse(url)) {
      return t('domain:Workspace.AvcTask.notValidUrl')
    }
    const urlObject = URL.parse(url)!
    return `${urlObject.hostname || urlObject.protocol}${urlObject.port.length ? `:${urlObject.port}` : ''}.../${[
      ...urlObject.pathname.split('/'),
    ]
      .reverse()
      .at(0)}`
  }

  if (physicalOutput) {
    if (sdi) {
      const avcInstance = avcInstancesBatchQuery.data?.items.find((avcInstance) =>
        avcInstance.capabilities.avio.sdi?.map((item) => item.id).includes(sdi.device),
      )
      const sdiInputName = avcInstance?.capabilities.avio.sdi?.find((item) => item.id === sdi.device)

      return `${sdiInputName?.name} (${avcInstance?.name})`
    }
    if (ndi) {
      const avcInstance = avcInstancesBatchQuery.data?.items.find((avcInstance) =>
        avcInstance.capabilities.avio.ndi?.map((item) => item.name).includes(ndi.name),
      )

      if (!avcInstance && !!ndi.name.length) {
        return ndi.name
      }

      const ndiInputName = avcInstance?.capabilities.avio.ndi?.find((item) => item.name === ndi.name)

      return `${ndiInputName?.name} (${avcInstance?.name})`
    }
  }

  return null
}

export const SecondaryInformation = memo(SecondaryInformationRaw)
